import React, { useCallback, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { showToast, dismissToast } from "../../Forms/Dialogs/Toast/Toast";
import { TextButton, SeemaOK } from "@SeemplicityDev/shared";

export const STREAMING_TOAST_ID = "streaming-chat-toast";
export const STREAMING_TOAST_DELAY_MS = 10000;
export const DONE_CHAT_TOAST_ID = "done-chat-toast";

const WorkingToastContent: React.FC = () => (
  <div className="toast-box-wrapper">
    <SeemaOK />
    <div className="toast-message-wrap">
      <div className="toast-message">Seema is working</div>
      <div className="toast-description">
        {`You can keep on using Seemplicity - we'll notify you when she's done`}
      </div>
    </div>
  </div>
);

const DoneToastContent: React.FC<{ chatId: string }> = ({ chatId }) => (
  <div className="toast-box-wrapper">
    <SeemaOK />
    <div className="toast-message-wrap chat-stream-toast-link">
      <div className="toast-message">Your chat response is ready.</div>
      <Link to={`/seema/${chatId}`}>
        <TextButton label="View response" buttonStyle="textLink" />
      </Link>
    </div>
  </div>
);

interface UseStreamNotificationsReturn {
  cancelStreamingToast: () => void;
  scheduleWorkingToastIfOnChatPage: (chatId: string) => void;
  showDoneToast: (chatId: string) => void;
}

export const useStreamNotifications = (): UseStreamNotificationsReturn => {
  const locationRef = useRef("");
  const streamingToastTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );

  const location = useLocation();

  useEffect(() => {
    locationRef.current = location.pathname;
  }, [location.pathname]);

  const cancelStreamingToast = useCallback(() => {
    if (streamingToastTimerRef.current !== null) {
      clearTimeout(streamingToastTimerRef.current);
      streamingToastTimerRef.current = null;
    }
    dismissToast(STREAMING_TOAST_ID);
  }, []);

  useEffect(() => {
    return () => {
      cancelStreamingToast();
    };
  }, [cancelStreamingToast]);

  const scheduleWorkingToastIfOnChatPage = useCallback((chatId: string) => {
    if (!locationRef.current.startsWith(`/seema/${chatId}`)) {
      return;
    }
    streamingToastTimerRef.current = setTimeout(() => {
      if (locationRef.current.startsWith(`/seema/${chatId}`)) {
        streamingToastTimerRef.current = null;
        showToast({
          position: "bottom-right",
          hideProgressBar: true,
          autoClose: 3000,
          closeOnClick: true,
          className: "toast-bottom-right toast-compact",
          toastId: STREAMING_TOAST_ID,
          draggable: false,
          component: <WorkingToastContent />,
        });
      }
    }, STREAMING_TOAST_DELAY_MS);
  }, []);

  const showDoneToast = useCallback((chatId: string) => {
    if (locationRef.current.startsWith(`/seema/${chatId}`)) {
      return;
    }
    showToast({
      hideProgressBar: false,
      autoClose: false,
      closeOnClick: true,
      toastId: DONE_CHAT_TOAST_ID,
      draggable: false,
      component: <DoneToastContent chatId={chatId} />,
    });
  }, []);

  return {
    cancelStreamingToast,
    scheduleWorkingToastIfOnChatPage,
    showDoneToast,
  };
};
